/**
 * The probe of a face check, made ready in the browser before it is sent (slice 01): decoded
 * upright (EXIF orientation applied), its long edge scaled down to about 2048 px, and
 * re-encoded as a JPEG, which also drops EXIF and GPS. Never scaled up: the quality gate
 * measures eye distance on these pixels, so a smaller probe would turn framing into "retake".
 */

/** Long edge of the probe the server analyses. */
export const PROBE_LONG_EDGE = 2048

const JPEG_QUALITY = 0.9

export interface PreparedProbe {
  /** JPEG, upright, without EXIF. */
  blob: Blob
  width: number
  height: number
  /** Decode, scale and encode, in ms (the "decode" of the latency split). */
  decodeMs: number
}

/** The file is not an image this browser can decode (e.g. HEIC in desktop Chrome). */
export class ProbeDecodeError extends Error {
  constructor() {
    super('The photo could not be decoded')
    this.name = 'ProbeDecodeError'
  }
}

/** The size that fits `max` on the long edge; never larger than the source. */
export function fitLongEdge(
  width: number,
  height: number,
  max: number = PROBE_LONG_EDGE
): { width: number; height: number } {
  const long = Math.max(width, height)
  if (long <= max) return { width, height }
  const scale = max / long
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

/** Upright bitmap; browsers that refuse the options bag decode without it. */
async function decode(file: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    try {
      return await createImageBitmap(file)
    } catch {
      throw new ProbeDecodeError()
    }
  }
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new ProbeDecodeError())),
      'image/jpeg',
      JPEG_QUALITY
    )
  })
}

/** Draw the bitmap at `size` and encode it as JPEG (off the DOM where the browser can). */
async function encodeJpeg(
  bitmap: ImageBitmap,
  size: { width: number; height: number }
): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(size.width, size.height)
    const context = canvas.getContext('2d')
    if (context) {
      context.imageSmoothingQuality = 'high'
      context.drawImage(bitmap, 0, 0, size.width, size.height)
      return canvas.convertToBlob({ type: 'image/jpeg', quality: JPEG_QUALITY })
    }
  }
  const canvas = document.createElement('canvas')
  canvas.width = size.width
  canvas.height = size.height
  const context = canvas.getContext('2d')
  if (!context) throw new ProbeDecodeError()
  context.imageSmoothingQuality = 'high'
  context.drawImage(bitmap, 0, 0, size.width, size.height)
  return canvasToBlob(canvas)
}

/** Decode, scale and re-encode a picked photo. Rejects with `ProbeDecodeError` when unreadable. */
export async function prepareProbe(file: Blob): Promise<PreparedProbe> {
  const started = performance.now()
  const bitmap = await decode(file)
  try {
    const size = fitLongEdge(bitmap.width, bitmap.height)
    const blob = await encodeJpeg(bitmap, size)
    return { blob, ...size, decodeMs: performance.now() - started }
  } finally {
    bitmap.close()
  }
}
